import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../gql/api';
import { getDeviceById, getDevicesByIds } from './devices';
import { getEventById, getEventsByIds } from './events';
import { getSituationById, getSituationsByIdsMinimal } from './situation';
import { addSituation } from '../slices/situations';
import { addDeviceHistory } from '../slices/deviceHistories';

const normalizeDevice = (device) => {
  if (!device) return null;

  return {
    id: device.id,
    name: device.name,
    image: device.image,
    uniqueId: device.uniqueId,
    workspacePrefix: { prefix: device.workspacePrefix.prefix },
    type: device.type,
    lastDevicePosition: device.lastDevicePosition,
    lastDeviceEvent: device.lastDeviceEvent || null,
    connectionStatus: device.connectionStatus || null,
    devicePlaces: device.devicePlaces || [],
  };
};

export const fetchSituationForMonitoring = createAsyncThunk(
  'monitoring/fetchSituationForMonitoring',
  async ({ situationId }, { dispatch }) => {
    const response = await api.monitoring.getSituationById({ situationId });
    const situation = response?.data?.getSituationById;

    if (!situation) {
      return null;
    }

    const device = normalizeDevice(situation.device);

    const { device: _unusedDevice, ...rest } = situation;
    const hydratedSituation = {
      ...rest,
      device,
      onHold: rest.onHold || false,
      log: rest.log?.data || [],
    };

    dispatch(addSituation(hydratedSituation));
    return hydratedSituation;
  }
);

/**
 * getAllSituationData
 * Actualiza el estado con la información de una situación,
 * la cual se busca en la API si no existe en Redux previamente
 */

// nueva version del metodo anterior

export const fetchSituationUsers = createAsyncThunk(
  'monitoring/fetchSituationUsers',
  async (users) => {
    const response = await Promise.all(
      users.map((userId) =>
        api.user.getUserById({
          userId,
        })
      )
    );
    const result = response.map((user) => user.data.getUserById);

    return result;
  },
  {
    condition: (users, { getState }) => {
      const usersInState = getState().users.ids;
      return !users.every((user) => usersInState.includes(user));
    },
  }
);

export const sendSituationOpenedNotification = createAsyncThunk(
  'monitoring/sendSituationOpenedNotification',
  async ({ openSituationId, userId }) => {
    const response = await api.monitoring.sendOpenNotification({
      openSituationId,
      userId,
    });

    return response.data.openSituation;
  }
);

export const freeDeviceHistory = createAsyncThunk(
  'monitoring/freeDeviceHistory',
  async ({ situationId, deviceHistoryId }) => {
    const response = await api.monitoring.freeDeviceHistory({
      freeDeviceHistoryId: deviceHistoryId,
      situationId,
    });

    const result = response.data.freeDeviceHistory;

    return {
      originalSituationId: situationId,
      id: deviceHistoryId,
      result: result, // Flattened data from backend
    };
  }
);

export const getSituationLogs = createAsyncThunk(
  'monitoring/getSituationLogs',
  async (situationId) => {
    const response = await api.monitoring.getSituationById({
      situationId: situationId,
    });
    console.log(
      '🚀 ~ file: monitoring.js:186 ~ response.data.getSituationById:',
      response.data.getSituationById
    );

    return response.data.getSituationById;
  }
);

export const commentSituation = createAsyncThunk(
  'monitoring/commentSituation',
  async ({ commentSituationId, comment, userId, phoneContact }) => {
    const response = await api.monitoring.commentSituation({
      commentSituationId,
      userId,
      comment,
      ...(phoneContact && { phoneContact: phoneContact }),
    });

    return response.data.commentSituation;
  }
);

export const processSituation = createAsyncThunk(
  'monitoring/processSituation',
  async ({ situationId, categoryId, comment }) => {
    const response = await api.monitoring.processSituation({
      processedSituationId: situationId,
      situationCategoryId: categoryId,
      comment,
    });

    return response.data.processedSituation;
  }
);

export const processSituations = createAsyncThunk(
  'monitoring/processSituations',
  async ({ ids, categoryId, comment }) => {
    const response = await api.monitoring.processSituations({
      ids,
      situationCategoryId: categoryId,
      comment,
    });

    const result = response.data.processedSituations;
    // Calculate successful IDs by excluding failed ones
    const failedIdsSet = new Set(result.failedIds || []);
    const successfulIds = ids.filter((id) => !failedIdsSet.has(id));

    return {
      ...result,
      successfulIds,
    };
  }
);

export const openSituationChat = createAsyncThunk(
  'monitoring/openSituationChat',
  async ({ situationId, userId }) => {
    const response = await api.monitoring.openSituationChat({
      situationId,
      userId,
    });

    return response.data.openConversation;
  }
);

export const openCallWindow = createAsyncThunk(
  'monitoring/openCallWindow',
  async ({ situationId, userId, phoneContact }) => {
    const response = await api.monitoring.openCallWindow({
      situationId,
      userId,
      phoneContact,
    });

    return response.data.openCallWindow;
  }
);

export const openCctvCamera = createAsyncThunk(
  'monitoring/openCctvCamera',
  async ({ situationId, userId, channelId, channelName }) => {
    const response = await api.monitoring.openCctvCamera({
      situationId,
      userId,
      channelId,
      channelName,
    });

    return response.data.openCctvCamera;
  }
);

export const checkCCTVChannelRound = createAsyncThunk(
  'monitoring/checkCCTVChannelRound',
  async ({ situationId, userId, channelId, channelName, comment }) => {
    const response = await api.monitoring.checkCCTVChannelRound({
      situationId,
      userId,
      channelId,
      channelName,
      ...(comment && { comment }),
    });

    return response.data.checkCCTVChannelRound;
  }
);

export const updateMonitoringStatus = createAsyncThunk(
  'monitoring',
  async (payload) => {
    const response = await api.monitoring.updateMonitoringStatus({
      data: payload,
    });

    return response.data.updateStatusMonitoring;
  }
);

export const checkMonitoringHealth = createAsyncThunk(
  'monitoring/checkHealth',
  async ({ userId, workspaceId }) => {
    const status = await api.monitoring.checkHealth({ userId, workspaceId });
    return status;
  }
);

export const fetchMissingEntities = createAsyncThunk(
  'situations/fetchMissingEntities',
  async (data, { dispatch, getState }) => {
    const state = getState();
    const deviceHistories = data?.deviceHistories ?? [data?.deviceHistory];

    const eventIds = [...new Set(deviceHistories.map((dh) => dh.eventId))];
    const deviceIds = [...new Set(deviceHistories.map((dh) => dh.deviceId))];
    const situationIds = [
      ...new Set(deviceHistories.map((dh) => dh.situationId)),
    ];
    const missingEventIds = eventIds.filter(
      (id) => id && !state.events.entities[id]
    );

    const missingDeviceIds = deviceIds.filter(
      (id) => id && !state.devices.entities[id]
    );

    const missingSituationIds = situationIds.filter(
      (id) => id && !state.situations.entities[id]
    );

    const eventsToFetch = [];

    if (missingEventIds.length > 0) {
      for (const eventId of missingEventIds) {
        eventsToFetch.push(dispatch(getEventById({ id: eventId })));
      }
    }

    await Promise.all(eventsToFetch);

    const devicesToFetch = [];
    if (missingDeviceIds.length > 0) {
      for (const deviceId of missingDeviceIds) {
        devicesToFetch.push(dispatch(getDeviceById({ id: deviceId })));
      }
    }

    await Promise.all(devicesToFetch);

    if (missingSituationIds.length > 0) {
      await dispatch(
        getSituationsByIdsMinimal({ situationIds: missingSituationIds })
      );
    }
  }
);
export const fetchMissingEntitiesTraffic = createAsyncThunk(
  'situations/fetchMissingEntitiesTraffic',
  async (data, { dispatch, getState }) => {
    const state = getState();
    const deviceHistories = data?.deviceHistories ?? [data?.deviceHistory];

    const eventIds = [...new Set(deviceHistories.map((dh) => dh.eventId))];
    const deviceIds = [...new Set(deviceHistories.map((dh) => dh.deviceId))];
    const situationIds = [
      ...new Set(deviceHistories.map((dh) => dh.situationId)),
    ];
    const missingEventIds = eventIds.filter(
      (id) => id && !state.events.entities[id]
    );

    const missingDeviceIds = deviceIds.filter(
      (id) => id && !state.devices.entities[id]
    );

    const missingSituationIds = situationIds.filter(
      (id) => id && !state.situations.entities[id]
    );

    if (missingEventIds.length > 0) {
      await dispatch(getEventsByIds({ eventIds: missingEventIds }));
    }

    if (missingDeviceIds.length > 0) {
      await dispatch(getDevicesByIds({ deviceIds: missingDeviceIds }));
    }

    if (missingSituationIds.length > 0) {
      await dispatch(
        getSituationsByIdsMinimal({ situationIds: missingSituationIds })
      );
    }
  }
);

export const handleIncomingDeviceHistory = createAsyncThunk(
  'deviceHistories/handleIncoming',
  async (data, { dispatch }) => {
    await dispatch(fetchMissingEntities(data));
    dispatch(addDeviceHistory(data));
  }
);

export const holdSituation = createAsyncThunk(
  'monitoring/holdSituation',
  async ({ holdSituationId, userId }) => {
    const response = await api.monitoring.holdSituation({
      holdSituationId,
      userId,
    });

    return response.data.holdSituation;
  }
);

export const generateCallSummary = createAsyncThunk(
  'monitoring/generateCallSummary',
  async ({ situationId, callId }) => {
    const response = await api.monitoring.generateCallSummary({
      situationId,
      callId,
    });

    return response.data.generateCallSummary;
  }
);
